import classNames from 'classnames';
import React, { useEffect, useMemo, memo, useCallback } from 'react';
import { useAppDispatch } from '@/store';
import { updateGraphicTypesSelectionHistory } from '../../actions/customization.actions';
import { GRAPHIC_TYPES_VALUES } from '../../constants/customization.constants';

const printingTypes = GRAPHIC_TYPES_VALUES;

const CustomPrintingTypes = ({
  selected,
  setSelected,
  itemTypes,
  productTypes,
  activeColorName,
  trackHistory,
  isProductLevel,
  inline,
  centered,
}) => {
  const dispatch = useAppDispatch();

  const customTypesHandler = useCallback(
    (graphicType) => () => {
      itemTypes.indexOf(graphicType) >= 0 && setSelected(graphicType);
      trackHistory && dispatch(updateGraphicTypesSelectionHistory(graphicType));
    },
    [itemTypes, selected, trackHistory, dispatch]
  );

  const isSelected = (t) => (Array.isArray(selected) ? selected.includes(t) : selected === t);

  const structuredTypes = useMemo(
    () =>
      isProductLevel
        ? printingTypes.filter((printingType) => productTypes.indexOf(printingType.value) >= 0)
        : printingTypes.filter((printingType) => itemTypes.indexOf(printingType.value) >= 0),
    [isProductLevel, productTypes, itemTypes, printingTypes]
  );

  useEffect(() => {
    if (itemTypes.every((t) => !isSelected(t))) {
      customTypesHandler(itemTypes[0])();
    }
  }, [selected, itemTypes]);

  return (
    <ul
      className={classNames('tw:flex tw:list-none tw:pl-0', {
        'tw:justify-center': centered,
      })}>
      {structuredTypes.map((item) => (
        <li key={item.value} className='tw:mt-2.5 tw:mr-2.5 tw:block tw:w-1/2 tw:last:mr-0'>
          <div
            className={classNames(
              'tw:flex tw:cursor-pointer tw:items-center tw:justify-center tw:rounded tw:border tw:bg-white tw:p-1.5 tw:transition-all tw:duration-300 tw:flex-col tw:gap-0.5',
              {
                'tw:justify-start tw:rounded-lg tw:p-2.5 tw:flex-row tw:gap-0': inline,
                'tw:border-2 tw:border-core-flame tw:p-1': isSelected(item.value),
                'tw:cursor-not-allowed tw:border-gray-300': itemTypes.indexOf(item.value) < 0,
                'tw:border-gray-300': !isSelected(item.value) && itemTypes.indexOf(item.value) >= 0,
              }
            )}
            onClick={customTypesHandler(item.value)}>
            <img
              src={item.image}
              alt={item.alt}
              className={classNames('tw:mr-1.5 tw:h-6', {
                'tw:mr-4': inline,
                'tw:opacity-50 tw:grayscale': itemTypes.indexOf(item.value) < 0,
              })}
            />
            <span
              className={classNames('tw:block tw:text-center tw:text-xs tw:font-normal', {
                'tw:mt-0 tw:text-sm': inline,
                'tw:text-gray-600': itemTypes.indexOf(item.value) < 0,
                'tw:text-body': itemTypes.indexOf(item.value) >= 0,
              })}>
              {item.text}
            </span>
          </div>
          {isProductLevel &&
            itemTypes.indexOf(item.value) === 0 &&
            itemTypes.length === 1 &&
            structuredTypes.length > 1 && (
              <p className='tw:mt-1.5 tw:mb-0 tw:text-left tw:text-xs tw:leading-4 tw:font-normal tw:text-red-500'>
                {activeColorName} is only available for {item.fullText}.
              </p>
            )}
        </li>
      ))}
    </ul>
  );
};

CustomPrintingTypes.defaultProps = {
  activeColorName: 'This variant',
  trackHistory: false,
  inline: false,
  isProductLevel: false,
  centered: false,
  itemTypes: [],
  productTypes: [],
};

export default memo(CustomPrintingTypes);
